import React, {useEffect, useMemo, useRef, useState} from "react";
import * as Sentry from '@sentry/browser';
import {withAuth} from "../../hoc/Auth";
import {Column, Container, Wrapper} from "../../hoc/Layout";
import Api from "../../Api";
import classes from "./styles.module.css";

const {motion, AnimatePresence} = require("framer-motion");

/* ------------------ helpers ------------------ */

function get(obj, path, def) {
    try {
        var parts = path.split(".");
        var cur = obj;
        for (var i = 0; i < parts.length; i++) {
            if (!cur || typeof cur !== "object") return def;
            cur = cur[parts[i]];
        }
        return typeof cur === "undefined" ? def : cur;
    } catch (e) {
        return def;
    }
}

function pretty(obj) {
    try {
        return JSON.stringify(obj, null, 2);
    } catch (e) {
        return String(obj);
    }
}

/* ------------------ UI schema ------------------ */

function buildLocalSchema(currencyOptions) {
    const authRequired = [
        {
            name: "merchantUuid",
            label: "UUID мерчанта",
            type: "text",
            required: true,
            hint: "Подставится из списка ниже"
        },
        {name: "apiKey", label: "API ключ", type: "text", required: true, hint: "Подставится из списка ниже"},
    ];

    const currencyField = {
        name: "currency",
        label: "Валюта",
        type: "select",
        options: currencyOptions && currencyOptions.length ? currencyOptions : ["RUB", "USD", "EUR"],
        required: true,
    };

    return {
        providers: [
            {key: "hydracode", label: "Гидрокод"},
            {key: "tkb", label: "ТКБ"},
            {key: "finstar", label: "Финстар"},
        ],
        operations: [
            {
                key: "get_balance",
                label: "Проверить баланс",
                hint: "Нужны только UUID мерчанта и его API ключ.",
                fields: authRequired,
            },
            {
                key: "get_tx_status",
                label: "Проверить статус транзакции",
                hint: "Укажите либо ID транзакции, либо внешний номер заказа.",
                fields: authRequired.concat([
                    {
                        name: "transactionId",
                        label: "ID транзакции",
                        type: "text",
                        required: false,
                        placeholder: "Напр. 123456"
                    },
                    {
                        name: "externalReference",
                        label: "Внешний номер заказа",
                        type: "text",
                        required: false,
                        placeholder: "Напр. order_123"
                    },
                ]),
            },
            // Платёж/выплата картой
            {
                key: "create_tx_card",
                label: "Создать платеж (КАРТА)",
                hint: "Введите сумму и валюту. Для Hydracode номер карты не требуется (будет редирект/реквизиты).",
                fields: authRequired.concat([
                    {name: "amount", label: "Сумма", type: "number", required: true, placeholder: "Напр. 1000"},
                    currencyField,
                    {
                        name: "cardPan",
                        label: "Номер карты",
                        type: "text",
                        required: true,
                        placeholder: "4111111111111111"
                    },
                    {
                        name: "phone",
                        label: "Телефон клиента",
                        type: "text",
                        required: false,
                        placeholder: "+79990000000"
                    },
                    {
                        name: "email",
                        label: "Email клиента",
                        type: "text",
                        required: false,
                        placeholder: "ivan@example.com"
                    },
                    {
                        name: "externalReference",
                        label: "Внешний номер заказа",
                        type: "text",
                        required: false,
                        placeholder: "order_123456"
                    },
                    {
                        name: "description",
                        label: "Описание",
                        type: "text",
                        required: false,
                        placeholder: "Тестовый платёж"
                    },
                    {
                        name: "callbackUrl",
                        label: "Callback URL",
                        type: "text",
                        required: false,
                        placeholder: "https://example.com/cb"
                    },
                ]),
            },
            // Платёж/выплата СБП
            {
                key: "create_tx_sbp",
                label: "Создать платеж (СБП)",
                hint: "Введите сумму и валюту. Для FinStar понадобится Bank Member ID и телефон.",
                fields: authRequired.concat([
                    {name: "amount", label: "Сумма", type: "number", required: true, placeholder: "Напр. 1000"},
                    currencyField,
                    // будет обязательным только для FinStar (динамически)
                    {
                        name: "sbpBankMemberId",
                        label: "SBP Bank Member ID",
                        type: "text",
                        required: false,
                        placeholder: "Напр. 100000000111"
                    },
                    {
                        name: "phone",
                        label: "Телефон клиента (СБП)",
                        type: "text",
                        required: false,
                        placeholder: "+79990000000"
                    },
                    {
                        name: "email",
                        label: "Email клиента",
                        type: "text",
                        required: false,
                        placeholder: "ivan@example.com"
                    },
                    {
                        name: "externalReference",
                        label: "Внешний номер заказа",
                        type: "text",
                        required: false,
                        placeholder: "order_123456"
                    },
                    {
                        name: "description",
                        label: "Описание",
                        type: "text",
                        required: false,
                        placeholder: "Тестовый платёж"
                    },
                    {
                        name: "callbackUrl",
                        label: "Callback URL",
                        type: "text",
                        required: false,
                        placeholder: "https://example.com/cb"
                    },
                ]),
            },
        ],
    };
}

/* ------------------ page ------------------ */

function Console(props) {
    var api = new Api({token: props.token});

    // currencies, merchants loading before rendering the form
    const [currencies, setCurrencies] = useState([]);
    const [currLoading, setCurrLoading] = useState(true);

    const [allMerchants, setAllMerchants] = useState([]);
    const [merchantsLoading, setMerchantsLoading] = useState(true);

    // schema based on loaded currencies
    const [schema, setSchema] = useState(buildLocalSchema([]));

    // selections
    const [provider, setProvider] = useState("hydracode");
    const [operation, setOperation] = useState("get_balance");

    // form & ui
    const [form, setForm] = useState({});
    const [sending, setSending] = useState(false);
    const [resp, setResp] = useState(null);
    const [err, setErr] = useState(null);

    // merchant picker state
    const [mList, setMList] = useState([]);
    const [mQuery, setMQuery] = useState("");
    const [pickerOpen, setPickerOpen] = useState(false);
    const pickerRef = useRef(null);

    // recent tx for selected merchant
    const [recentTx, setRecentTx] = useState([]);
    const [txLoading, setTxLoading] = useState(false);

    // center content container size
    const centerWrapStyle = {
        minHeight: "calc(100vh - 120px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    };

    // load currencies from backend (graceful fallback)
    useEffect(function () {
        var mounted = true;
        setCurrLoading(true);
        api.client
            .get("/api/ui/currencies", {params: {limit: 500}})
            .then(function ({data}) {
                if (!mounted) return;
                var items = Array.isArray(data) ? data : Array.isArray(get(data, "items", [])) ? data.items : [];
                var opts = items
                    .map(function (c) {
                        return c && (c.code || c.Code || c.CODE);
                    })
                    .filter(function (v) {
                        return !!v;
                    });
                setCurrencies(opts);
                setSchema(buildLocalSchema(opts));
            })
            .catch(function (err) {
                Sentry.captureException(err);
                setCurrencies(["RUB", "USD", "EUR"]);
                setSchema(buildLocalSchema(["RUB", "USD", "EUR"]));
            })
            .finally(function () {
                if (mounted) setCurrLoading(false);
            });
        return function () {
            mounted = false;
        };
    }, []);

    // load merchants once
    useEffect(function () {
        var cancelled = false;
        setMerchantsLoading(true);
        api.client
            .get("/api/ui/merchants", {params: {limit: 2000}})
            .then(function ({data}) {
                if (cancelled) return;
                const items = Array.isArray(data && data.items) ? data.items : [];
                setAllMerchants(items);
                setMList(items.slice(0, 15));
            })
            .catch(function (err) {
                Sentry.captureException(err);
                setAllMerchants([]);
                setMList([]);
            })
            .finally(function () {
                if (!cancelled) setMerchantsLoading(false);
            });
        return function () {
            cancelled = true;
        };
    }, []);

    // outside-click to close picker
    useEffect(function () {
        function onDoc(e) {
            try {
                if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                    setPickerOpen(false);
                }
            } catch (e2) {
            }
        }

        document.addEventListener("mousedown", onDoc);
        return function () {
            document.removeEventListener("mousedown", onDoc);
        };
    }, []);

    // computed
    function fieldsForCurrent() {
        var ops = get(schema, "operations", []);
        for (var i = 0; i < ops.length; i++) if (ops[i].key === operation) return ops[i];
        return {fields: []};
    }

    const current = fieldsForCurrent();
    const baseFields = current.fields || [];

    // динамическая адаптация полей под провайдера:
    function activeFields() {
        var fs = baseFields.slice();

        // Для Hydracode не нужен cardPan (он вводится на стороне провайдера)
        if (provider === "hydracode") {
            fs = fs.filter(function (f) {
                return f.name !== "cardPan";
            });
        }

        // Для FinStar/СБП — поле bank_member_id обязательно;
        // для других провайдеров прячем его.
        if (operation === "create_tx_sbp") {
            if (provider !== "finstar") {
                fs = fs.filter(function (f) {
                    return f.name !== "sbpBankMemberId";
                });
            } else {
                fs = fs.map(function (f) {
                    if (f.name === "sbpBankMemberId") {
                        var g = Object.assign({}, f);
                        g.required = true;
                        g.hint = g.hint || "Обязательное поле для выплат СБП через Финстар.";
                        return g;
                    }
                    if (f.name === "phone") {
                        var p = Object.assign({}, f);
                        p.required = true;
                        p.hint = p.hint || "Телефон получателя в формате +7… (цифры будут нормализованы).";
                        return p;
                    }
                    return f;
                });
            }
        }

        return fs;
    }

    const fields = activeFields();
    const isCreateOp = operation === "create_tx_card" || operation === "create_tx_sbp";
    const hasCurrencyField = fields.some(function (f) {
        return f.name === "currency";
    });

    function onChangeField(name, value) {
        if (name === "cardPan" && typeof value === "string") value = value.replace(/\s+/g, "");
        if (name === "currency" && typeof value === "string") value = value.toUpperCase();
        setForm(function (p) {
            var c = {};
            for (var k in p) if (Object.prototype.hasOwnProperty.call(p, k)) c[k] = p[k];
            c[name] = value;
            return c;
        });
    }

    function validate() {
        var miss = [];
        for (var i = 0; i < fields.length; i++) {
            var f = fields[i];
            if (f.required) {
                var v = form[f.name];
                if (v === undefined || v === null || v === "") miss.push(f.label || f.name);
            }
        }
        return miss;
    }

    function buildPayload() {
        var base = {provider: provider, action: operation, data: Object.assign({}, form)};
        if (operation === "create_tx_card") base.data.direction = "card";
        if (operation === "create_tx_sbp") base.data.direction = "sbp";

        // Для Hydracode не отправляем cardPan
        if (provider === "hydracode" && base.data.cardPan) {
            try {
                delete base.data.cardPan;
            } catch (e) {
            }
        }

        // числовая сумма
        if (typeof base.data.amount === "string" && base.data.amount.trim() !== "") {
            const n = Number(base.data.amount.replace(",", "."));
            if (!Number.isNaN(n)) base.data.amount = n;
        }
        // дефолт валюты, если нужна
        if (!base.data.currency && hasCurrencyField) base.data.currency = (currencies && currencies[0]) || "RUB";
        return base;
    }

    function submit() {
        setErr(null);
        setResp(null);
        var miss = validate();
        if (miss.length) {
            setErr("Заполните обязательные поля: " + miss.join(", "));
            return;
        }
        setSending(true);
        api.uiExecute(buildPayload())
            .then(function (data) {
                setResp(data);
            })
            .catch(function (err) {
                Sentry.captureException(err);
                setErr("Ошибка запроса");
            })
            .finally(function () {
                setSending(false);
            });
    }

    // merchant picker (локальный фильтр)
    function onMerchantInput(v) {
        setMQuery(v);
        const t = (v || "").toLowerCase().trim();
        const src = allMerchants;
        const out = !t
            ? src.slice(0, 15)
            : src
                .filter(function (m) {
                    return (
                        (m.name || "").toLowerCase().includes(t) ||
                        (m.uuid || "").toLowerCase().includes(t)
                    );
                })
                .slice(0, 15);
        setMList(out);
    }

    function pickMerchant(m) {
        setMQuery(m.name);
        setMList([]);
        setPickerOpen(false);
        onChangeField("merchantUuid", m.uuid);
        onChangeField("apiKey", m.apiKey);
        // авто-подстановка валюты из бэка (если доступна)
        fetchPreferredCurrency(m.uuid);
        // подгрузить последние транзакции мерчанта (для быстрой проверки)
        loadRecentTx(m.uuid);
        // автофокус на следующем обязательном поле
        setTimeout(function () {
            try {
                const firstReq = fields.find(function (f) {
                    return f.required && f.name !== "merchantUuid" && f.name !== "apiKey";
                });
                if (firstReq) {
                    const el = document.querySelector('[data-field="' + firstReq.name + '"]');
                    if (el) el.focus();
                }
            } catch (e) {
            }
        }, 0);
    }

    function randomRef() {
        const r = "order_" + Date.now();
        onChangeField("externalReference", r);
    }

    function clearForm(keepCreds) {
        setResp(null);
        setErr(null);
        setForm(function (p) {
            const next = {};
            if (keepCreds) {
                if (p.merchantUuid) next.merchantUuid = p.merchantUuid;
                if (p.apiKey) next.apiKey = p.apiKey;
            }
            return next;
        });
    }

    function onOperationChange(v) {
        setOperation(v);
        setForm(function (p) {
            const next = {};
            if (p.merchantUuid) next.merchantUuid = p.merchantUuid;
            if (p.apiKey) next.apiKey = p.apiKey;
            return next;
        });
        setErr(null);
        setResp(null);
    }

    function renderFields() {
        if (!fields.length) {
            return <div className={classes.FormInfo}>Поля не требуются.</div>;
        }
        return (
            <div className={classes.Grid}>
                {/* ШАГ 1: мерчант */}
                <div className={classes.Field} key="merchantPicker" ref={pickerRef}>
                    <label className={classes.Label}>1) Выберите мерчанта по имени</label>
                    <input
                        className={classes.Input}
                        type="text"
                        value={mQuery}
                        placeholder={allMerchants.length ? "Начните вводить… (доступно: " + allMerchants.length + ")" : merchantsLoading ? "Идет загрузка…" : "Мерчанты не найдены"}
                        onChange={function (e) {
                            onMerchantInput(e.target.value);
                        }}
                        onFocus={function () {
                            setPickerOpen(true);
                        }}
                        onClick={function () {
                            if (!pickerOpen) setPickerOpen(true);
                        }}
                    />

                    <AnimatePresence>
                        {pickerOpen && mList.length ? (
                            <motion.div
                                key="drop"
                                initial={{opacity: 0, y: -6}}
                                animate={{opacity: 1, y: 0}}
                                exit={{opacity: 0, y: -6}}
                                transition={{duration: 0.18}}
                                className={classes.Drop}
                            >
                                {mList.map(function (m) {
                                    return (
                                        <div key={m.id} className={classes.DropItem} onClick={function () {
                                            pickMerchant(m);
                                        }}>
                                            <div className={classes.DropTitle}>{m.name}</div>
                                            <div className={classes.DropSub}>{m.uuid}</div>
                                        </div>
                                    );
                                })}
                            </motion.div>
                        ) : null}
                    </AnimatePresence>

                    <div className={classes.FormInfo}>При выборе мерчанта его UUID и API ключ подставятся в форму.</div>
                </div>

                {/* Остальные поля формы */}
                {fields.map(function (f) {
                    var v = Object.prototype.hasOwnProperty.call(form, f.name) ? form[f.name] : "";
                    var label = f.label || f.name;
                    var isSelect = f.type === "select";
                    var isNum = f.type === "number";
                    var req = !!f.required;
                    var placeholder = f.placeholder || (f.name === "merchantUuid" ? "uuid будет подставлен" : (f.name === "apiKey" ? "apiKey будет подставлен" : ""));

                    return (
                        <div className={classes.Field} key={f.name}>
                            <label className={classes.Label}>
                                {label}
                                {req ? <span className={classes.Required}> *</span> : null}
                            </label>

                            {isSelect ? (
                                <select
                                    className={classes.Select}
                                    data-field={f.name}
                                    value={v || ""}
                                    onChange={function (e) {
                                        onChangeField(f.name, e.target.value);
                                    }}
                                >
                                    <option value="">Выберите…</option>
                                    {(f.options || []).map(function (opt) {
                                        return (
                                            <option key={opt} value={opt}>{opt}</option>
                                        );
                                    })}
                                </select>
                            ) : (
                                <input
                                    className={classes.Input}
                                    data-field={f.name}
                                    type={isNum ? "number" : "text"}
                                    value={v}
                                    placeholder={placeholder}
                                    onChange={function (e) {
                                        onChangeField(f.name, e.target.value);
                                    }}
                                />
                            )}

                            {f.hint ? <div className={classes.FormInfo}>{f.hint}</div> : null}
                        </div>
                    );
                })}

                {/* Подсказки под формой */}
                {provider === "hydracode" && isCreateOp ? (
                    <div className={classes.FormInfo} style={{gridColumn: "1 / -1"}}>
                        Для Hydracode ввод номера карты не требуется — после отправки будет редирект/реквизиты оплаты.
                    </div>
                ) : null}

                {provider === "finstar" && operation === "create_tx_sbp" ? (
                    <div className={classes.FormInfo} style={{gridColumn: "1 / -1"}}>
                        Для FinStar/СБП обязательно заполните <b>SBP Bank Member ID</b> и <b>Телефон</b>.
                    </div>
                ) : null}
            </div>
        );
    }

    // Ответ (нормализованный + raw)
    var normalized = useMemo(function () {
        return resp && resp.normalized ? resp.normalized : null;
    }, [resp]);
    var raw = useMemo(function () {
        return resp && resp.raw ? resp.raw : resp;
    }, [resp]);

    // ready to show form only when base data loaded
    const ready = !currLoading && !merchantsLoading;

    // --------- helper loaders ---------
    function LoaderCard() {
        return (
            <motion.div initial={{opacity: 0, y: 6}} animate={{opacity: 1, y: 0}} className={classes.Card}>
                <div className={classes.Pre}>Загружаем данные для формы…</div>
            </motion.div>
        );
    }

    // --------- backend helpers ---------
    function fetchPreferredCurrency(uuid) {
        if (!uuid) return;
        api.client
            .get("/api/ui/merchant/" + uuid + "/preferred-currency")
            .then(function ({data}) {
                var c = data && (data.code || data.currency || data.Currency);
                if (c) onChangeField("currency", String(c).toUpperCase());
            })
            .catch(function (err) {
                Sentry.captureException(err);
            });
    }

    function loadRecentTx(uuid) {
        if (!uuid) return;
        setTxLoading(true);
        api.client
            .get("/api/ui/transactions/latest", {params: {merchant: uuid, limit: 5}})
            .then(function ({data}) {
                var items = Array.isArray(data && data.items) ? data.items : [];
                setRecentTx(items);
            })
            .catch(function (err) {
                Sentry.captureException(err);
                setRecentTx([]);
            })
            .finally(function () {
                setTxLoading(false);
            });
    }

    function selectTx(t) {
        var id = t && (t.id != null ? t.id : (t.transactionId != null ? t.transactionId : ""));
        var ext = t && (t.externalReference != null ? t.externalReference : (t.extRef != null ? t.extRef : ""));
        onChangeField("transactionId", id);
        onChangeField("externalReference", ext);
        setResp({normalized: {message: "Транзакция выбрана из списка", id: id, externalReference: ext}});
    }

    return (
        <Wrapper>
            <Container style={{justifyContent: 'center'}}>
                <Column width={1180}>
                    <div>
                        <div className={classes.Wrap} style={centerWrapStyle}>
                            <motion.div
                                initial={{opacity: 0, scale: 0.98}}
                                animate={{opacity: 1, scale: 1}}
                                style={{width: "100%", maxWidth: 980}}
                            >
                                <div
                                    className={classes.PageTitle}
                                    style={{textAlign: "center", marginBottom: 16}}
                                >
                                    Manager API Console
                                </div>

                                {!ready ? (
                                    <LoaderCard/>
                                ) : (
                                    <React.Fragment>
                                        <motion.div
                                            className={classes.Card}
                                            initial={{opacity: 0, y: 8}}
                                            animate={{opacity: 1, y: 0}}
                                        >
                                            {/* Шапка */}
                                            <div className={classes.Row2}>
                                                <div className={classes.Field}>
                                                    <label className={classes.Label}>Провайдер</label>
                                                    <select
                                                        className={classes.Select}
                                                        value={provider}
                                                        onChange={function (e) {
                                                            setProvider(e.target.value);
                                                        }}
                                                    >
                                                        {get(schema, "providers", []).map(function (p) {
                                                            return (
                                                                <option key={p.key} value={p.key}>
                                                                    {p.label || p.key}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                                <div className={classes.Field}>
                                                    <label className={classes.Label}>Действие</label>
                                                    <select
                                                        className={classes.Select}
                                                        value={operation}
                                                        onChange={function (e) {
                                                            onOperationChange(e.target.value);
                                                        }}
                                                    >
                                                        {get(schema, "operations", []).map(function (op) {
                                                            return (
                                                                <option key={op.key} value={op.key}>
                                                                    {op.label || op.key}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                    {current && current.hint ? (
                                                        <div className={classes.FormInfo} style={{marginTop: 6}}>
                                                            {current.hint}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>

                                            <div className={classes.Separator}/>

                                            {/* Поля */}
                                            {renderFields()}

                                            {/* Действия формы */}
                                            <div className={classes.Actions}>
                                                <button
                                                    className={classes.SecondaryBtn}
                                                    type="button"
                                                    onClick={function () {
                                                        clearForm(true);
                                                    }}
                                                >
                                                    Очистить форму (сохранить мерчанта)
                                                </button>

                                                {isCreateOp ? (
                                                    <button
                                                        className={classes.SecondaryBtn}
                                                        type="button"
                                                        onClick={randomRef}
                                                    >
                                                        Случайный reference
                                                    </button>
                                                ) : null}

                                                <div className={classes.ActionsSpacer}/>
                                                <button
                                                    className={classes.PrimaryBtn}
                                                    disabled={sending}
                                                    type="button"
                                                    onClick={submit}
                                                >
                                                    {sending ? "Отправка…" : "Отправить"}
                                                </button>
                                            </div>

                                            {err ? <div className={classes.Error}>{err}</div> : null}
                                        </motion.div>

                                        {/* Блок ответа + последние транзакции */}
                                        <div className={classes.Row2} style={{marginTop: 16}}>
                                            <motion.div
                                                className={classes.Card}
                                                style={{flex: 1}}
                                                initial={{opacity: 0, y: 8}}
                                                animate={{opacity: 1, y: 0}}
                                            >
                                                <div className={classes.BlockTitle}>Ответ</div>
                                                {!resp ? (
                                                    <pre className={classes.Pre}>Ответ появится здесь</pre>
                                                ) : (
                                                    <div>
                          <pre
                              className={classes.Pre}
                              style={{borderRadius: 8, padding: 12, fontSize: 13, overflow: "auto"}}
                          >
{pretty(normalized || resp)}
                          </pre>
                                                        <details className={classes.Details}>
                                                            <summary>RAW JSON</summary>
                                                            <pre
                                                                className={classes.Pre}
                                                                style={{
                                                                    borderRadius: 8,
                                                                    padding: 12,
                                                                    fontSize: 13,
                                                                    overflow: "auto"
                                                                }}
                                                            >
{pretty(raw)}
                            </pre>
                                                        </details>
                                                    </div>
                                                )}
                                            </motion.div>

                                            {operation === "get_tx_status" ? (
                                                <motion.div
                                                    className={classes.Card}
                                                    style={{flex: 1}}
                                                    initial={{opacity: 0, y: 8}}
                                                    animate={{opacity: 1, y: 0}}
                                                >
                                                    <div className={classes.BlockTitle}>
                                                        Последние транзакции мерчанта
                                                    </div>
                                                    {txLoading ? (
                                                        <div className={classes.Pre}>Загрузка…</div>
                                                    ) : !recentTx.length ? (
                                                        <div className={classes.FormInfo}>
                                                            Выберите мерчанта, чтобы увидеть 5 последних операций.
                                                        </div>
                                                    ) : (
                                                        <div className={classes.TableWrap}>
                                                            <table className={classes.Table}>
                                                                <thead>
                                                                <tr>
                                                                    <th>Дата</th>
                                                                    <th>Сумма</th>
                                                                    <th>Валюта</th>
                                                                    <th>Тип</th>
                                                                    <th>ID</th>
                                                                    <th>Reference</th>
                                                                    <th></th>
                                                                </tr>
                                                                </thead>
                                                                <tbody>
                                                                {recentTx.map(function (t, idx) {
                                                                    const dt = (t.date || t.Date || "").toString();
                                                                    const amount = t.amountFloat != null ? t.amountFloat : t.amount;
                                                                    const curr = (t.currency && (t.currency.code || t.currency)) || "";
                                                                    const type = (t.type && (t.type.name || t.type)) || "";
                                                                    const id = t.id != null ? t.id : (t.transactionId != null ? t.transactionId : "");
                                                                    const ext = t.externalReference != null ? t.externalReference : (t.extRef != null ? t.extRef : "");
                                                                    return (
                                                                        <tr key={idx}>
                                                                            <td>{dt}</td>
                                                                            <td>{amount}</td>
                                                                            <td>{curr}</td>
                                                                            <td>{type}</td>
                                                                            <td>{id}</td>
                                                                            <td>{ext}</td>
                                                                            <td>
                                                                                <button
                                                                                    className={classes.SecondaryBtn}
                                                                                    type="button"
                                                                                    onClick={function () {
                                                                                        selectTx(t);
                                                                                    }}
                                                                                >
                                                                                    Выбрать
                                                                                </button>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </motion.div>
                                            ) : null}
                                        </div>
                                    </React.Fragment>
                                )}
                            </motion.div>
                        </div>
                    </div>
                </Column>
            </Container>
        </Wrapper>
    );
}

export default withAuth(Console);



// WEBPACK FOOTER //
// ./src/PagesOfAdministrator/Postman/index.js